import React, { use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApolloClient } from '@apollo/client';
import { CombinedGraphQLErrors, ServerError } from '@apollo/client/errors';
import ReactGA from 'react-ga4';

import {
  GetViewerDocument,
  RenewTokenDocument,
  RenewTokenMutation,
  ViewerFragmentFragment,
} from '../api/graphql/__generated';
import { notifyIos } from '../api/ios';
import { userMonitor } from '../component/rum';
import { history } from '../util/history';
import {
  buildSearchParams,
  CLEAR_ALL_SEARCH_PARAMS,
  ContainerTypeLegacy,
  getQueryParameter,
  WorkerMessageType,
} from '../util';
import { sendMessage } from '@amber-ui/service-worker-client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type AuthState = {
  user_id: string | null;
  account_id: string | null;
  email_address: string;
  verified_email: boolean;
  ready: boolean;
};

export type AuthActions = {
  login: (viewer: ViewerFragmentFragment) => void;
  logout: (propagate: boolean) => void;
  renewToken: (token: string) => void;
};

export type AuthContextValue = AuthState & AuthActions;

const defaultAuthState: AuthState = {
  user_id: null,
  account_id: null,
  email_address: '',
  // Default true: prevents the _auth route guard from redirecting to /user/email_verification
  // during initialization (before token renewal completes and sets the real value).
  // The guard's `if (!auth.ready) return` check already prevents redirect during init,
  // but this default provides defense-in-depth against any code path that reads
  // verified_email before ready=true.
  verified_email: true,
  ready: false,
};

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

// Preserve the context object across HMR module re-evaluations.
// Without this, a file change that propagates through the module graph causes
// auth-context.tsx to re-execute, creating a NEW React.createContext() value.
// Components still referencing the old context get `undefined`, triggering
// "useAuth must be used within an AuthProvider". The import.meta.hot.data
// store persists across HMR updates of this specific module.
//
// Pattern validated against Vite's official HMR API docs (vite.dev/guide/api-hmr):
// "The import.meta.hot.data object is persisted across different instances of
// the same updated module. It can be used to pass on information from a previous
// version of the module to the next one."
export const AuthContext: React.Context<AuthContextValue | undefined> = (() => {
  // oxlint-disable-next-line typescript/no-explicit-any -- Vite HMR types not available in core package
  const hot = (import.meta as any).hot as { data: Record<string, any> } | undefined;
  if (hot?.data) {
    // Reuse the context from the previous module evaluation if available
    if (!hot.data.AuthContext) {
      hot.data.AuthContext = React.createContext<AuthContextValue | undefined>(undefined);
    }
    return hot.data.AuthContext;
  }
  // Production or test environment: no HMR data, create normally
  return React.createContext<AuthContextValue | undefined>(undefined);
})();

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

type AuthProviderProps = React.PropsWithChildren<{
  apolloClient: ApolloClient;
}>;

export function AuthProvider({ apolloClient, children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>(defaultAuthState);
  const initializedRef = useRef(false);

  // -------------------------------------------------------------------------
  // login — set session fields, write viewer to cache, persist token, set RUM
  // -------------------------------------------------------------------------
  const login = useCallback(
    (viewer: ViewerFragmentFragment) => {
      const { user_id, account_id, token, user } = viewer;
      const { email_address } = user;

      setState({
        user_id,
        account_id,
        email_address,
        verified_email: user.verified_email,
        ready: true,
      });

      // Set Google Analytics user for attribution
      ReactGA.set({ user_id });

      // Notify iOS native shell that the app is ready
      notifyIos({ message_type: 'ready' });

      // Write viewer to Apollo cache so components using GetViewer see data immediately
      apolloClient.writeQuery({
        query: GetViewerDocument,
        data: { __typename: 'Query' as const, viewer },
      });

      // Customer care impersonation: support staff has a customer_care_token in localStorage.
      // When impersonating, we don't overwrite the persisted token (it belongs to the
      // real user's session) and we don't set RUM user (Datadog attribution stays on
      // the original user who logged in).
      const isRealUser = !localStorage.getItem('customer_care_token');

      // Persist token to localStorage (only for real users, not impersonation)
      if (isRealUser) {
        window.localStorage.setItem('token', token);
      }
      window.localStorage.setItem('email_address', email_address);

      // Set RUM user for Datadog
      if (isRealUser) {
        const trackUserData = {
          id: viewer.user.user_id,
          email: viewer.user.email_address,
          name: `${viewer.user.first_name} ${viewer.user.last_name}`,
          account_id: viewer.account_id,
          organization: viewer.account.organization,
        };
        userMonitor.setUser(trackUserData);
        localStorage.setItem('initial_logged_in_user', JSON.stringify(trackUserData));
      } else {
        // Customer care impersonation: restore the real user's RUM identity from
        // localStorage so Datadog sessions are attributed to the support agent,
        // not the customer being impersonated.
        const initialLoggedInUser = localStorage.getItem('initial_logged_in_user');
        if (initialLoggedInUser) {
          const trackUserData = JSON.parse(initialLoggedInUser) as {
            id: string;
            email: string;
            name: string;
            account_id: string;
            organization: string;
          };
          userMonitor.setUser(trackUserData);
        }
      }
    },
    [apolloClient]
  );

  // -------------------------------------------------------------------------
  // logout — clear fields, clear Apollo store, remove tokens, redirect, broadcast
  // -------------------------------------------------------------------------
  const logout = useCallback(
    (propagate: boolean) => {
      userMonitor.removeUser();
      window.localStorage.removeItem('token');
      window.localStorage.removeItem('customer_care_token');
      window.localStorage.removeItem('initial_logged_in_user');

      // Clear Apollo store (returns a promise — fire-and-forget)
      apolloClient.clearStore().catch(() => {
        // Silent failure — store clearing is best-effort
      });

      setState({
        user_id: null,
        account_id: null,
        email_address: '',
        verified_email: true, // reset to safe default
        ready: true,
      });

      // Strip all container/account params when redirecting to login — they belong
      // to the authenticated session and are meaningless on the login page.
      history.replace('/login', { search: CLEAR_ALL_SEARCH_PARAMS });

      // Broadcast logout to other tabs via service worker
      if (propagate && navigator.serviceWorker) {
        sendMessage({
          message_type: WorkerMessageType.logout,
          payload: { propagate: true },
        }).catch(() => {
          // Silent failure — service worker may not be active
        });
      }
    },
    [apolloClient]
  );

  // -------------------------------------------------------------------------
  // renewToken — persist refreshed JWT to localStorage
  // -------------------------------------------------------------------------
  const renewToken = useCallback((token: string) => {
    window.localStorage.setItem('token', token);
  }, []);

  // -------------------------------------------------------------------------
  // Token renewal on mount (logic ported from the deleted loginByTokenSaga).
  // No cancellation/cleanup needed: AuthProvider wraps the entire app and never
  // unmounts during normal app lifecycle. The initializedRef guard handles React
  // strict mode double-mount in dev. React 18+ silently no-ops setState on
  // unmounted components, so even in edge cases there's no crash risk.
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    initializeAuth(apolloClient, login, logout, setState);
  }, [apolloClient, login, logout]);

  // -------------------------------------------------------------------------
  // Service worker logout broadcast listener.
  // This listener handles logout broadcasts from other tabs.
  // ServiceWorkerMessageHandler also receives logout messages but no-ops on them
  // (break without action) — the split is intentional: auth state changes
  // are AuthProvider's responsibility, all other message types are handled
  // by ServiceWorkerMessageHandler.
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!navigator.serviceWorker) return;

    const handleMessage = (event: MessageEvent) => {
      const message = event.data as { message_type?: string; payload?: { propagate?: boolean } };
      if (message.message_type === WorkerMessageType.logout) {
        logout(false);
      }
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage);
    };
  }, [logout]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  const value = useMemo<AuthContextValue>(
    () => ({ ...state, login, logout, renewToken }),
    [state, login, logout, renewToken]
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAuth(): AuthContextValue {
  const context = use(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// ---------------------------------------------------------------------------
// Initialization logic (ported from the deleted loginByTokenSaga)
// ---------------------------------------------------------------------------

/**
 * Token-based authentication on mount.
 * Supports three token sources (in priority order):
 * 1. URL query param (?token=...) — used by email deep links
 * 2. customer_care_token in localStorage — used by support staff impersonating users
 * 3. Regular user token in localStorage — normal session resumption
 *
 * On failure, distinguishes BadTokenError (expired/invalid link → end session)
 * from other ServerErrors (infrastructure issue → redirect to /error page).
 */
function initializeAuth(
  apolloClient: ApolloClient,
  login: (viewer: ViewerFragmentFragment) => void,
  logout: (propagate: boolean) => void,
  setState: React.Dispatch<React.SetStateAction<AuthState>>
): void {
  // Skip auth initialization for SSO callback — let the callback route handle token exchange.
  // Without this bypass, a user who was previously logged in (token in localStorage) would
  // have their old token renewed successfully, then get redirected away from /sso/callback
  // before the SSO token exchange happens.
  const ssoPathname = window.location.pathname;
  const ssoSearchParams = new URLSearchParams(window.location.search);
  if (ssoPathname.startsWith('/sso/callback') && ssoSearchParams.has('stytch_token_type')) {
    // Setting ready=true immediately allows the router to render the /sso/callback route
    // component, which handles the Stytch token exchange and localStorage.setItem + reload.
    setState((prev) => ({ ...prev, ready: true }));
    notifyIos({ message_type: 'ready' });
    return;
  }

  // Token priority: URL param > customer care impersonation > regular user session
  const hasQueryParamsToken = window.location.href.includes('?token=');
  const customerCareToken = localStorage.getItem('customer_care_token');
  const userToken = customerCareToken ? customerCareToken : localStorage.getItem('token');
  const token = hasQueryParamsToken ? getQueryParameter('token') : userToken;

  if (token) {
    // RenewToken takes no variables — the existing JWT token is sent via the
    // Authorization header (set by SetContextLink in client.ts). The server
    // validates the header token and returns a fresh viewer with a new JWT.
    apolloClient
      .mutate({
        mutation: RenewTokenDocument,
        errorPolicy: 'all',
        update: (store, { data: mutationData }) => {
          if (mutationData) {
            store.writeQuery({
              query: GetViewerDocument,
              data: { __typename: 'Query' as const, viewer: mutationData.renewToken },
            });
          }
        },
      })
      .then(({ data, error: mutationError }) => {
        if (mutationError) {
          // Check if this is an SSO re-authentication requirement (not a real auth failure).
          // SSO-enforced accounts have 7-day tokens that cannot be silently renewed.
          // Uses AC4's CombinedGraphQLErrors.is() type guard — the canonical pattern
          // in this codebase (see client.ts ErrorLink, _grain-bin.tsx loader).
          if (CombinedGraphQLErrors.is(mutationError)) {
            const isSSOReauth = mutationError.errors.some(
              (e) => e.extensions?.code === 'SSO_REAUTH_REQUIRED'
            );
            if (isSSOReauth) {
              // Redirect to login page where user can click "Sign in with SSO".
              // The IdP will auto-authenticate if their session is still active (~2-3s redirect).
              // No setState({ ready: true }) needed — window.location.replace destroys the
              // React tree. ready: false during the brief navigation window is intentional
              // (prevents auth guard from flashing content).
              window.localStorage.removeItem('token');
              window.location.replace('/login');
              return;
            }
          }
          logout(true);
          return;
        }
        if (data) {
          const { renewToken: viewer } = data;
          login(viewer);

          // After successful login, redirect away from transient paths (login, error, root)
          // to the appropriate dashboard. This replaces the deleted createSessionSaga's
          // isTransientPath() + history.replace(redirectTarget) logic.
          //
          // The viewer's selected_grain_bin tells us what bin/account to load.
          // We push acct + container params to the URL so the route tree and
          // retainSearchParams middleware preserve them across subsequent navigations.
          const pathname = window.location.pathname;
          if (pathname === '/' || pathname.startsWith('/error') || pathname.startsWith('/login')) {
            const selectedBin = viewer.user.selected_grain_bin;
            const search = buildSearchParams({
              // Use the selected bin's account_id (not viewer.account_id) because
              // system admins can have their last-selected bin in a different account
              // than their home account. The sidebar uses acct to determine which
              // account's bins to display.
              account_id: selectedBin?.account_id ?? viewer.account_id,
              container_id: selectedBin?.container_id ?? null,
              container_type:
                selectedBin?.container_type != null
                  ? (selectedBin.container_type as ContainerTypeLegacy)
                  : null,
            });
            // Route directly to the appropriate dashboard based on the viewer's
            // last container selection. Don't use '/' — the bare root renders the
            // auth layout shell without any dashboard content.
            let target: string;
            if (selectedBin?.container_id && selectedBin.container_type != null) {
              switch (selectedBin.container_type as ContainerTypeLegacy) {
                case ContainerTypeLegacy.barge:
                  target = '/barge/dashboard';
                  break;
                case ContainerTypeLegacy.pile:
                  target = '/pile/dashboard';
                  break;
                default:
                  target = '/grain_bin/dashboard';
                  break;
              }
            } else {
              target = '/grain/operations';
            }
            history.replace(target, { search });
          }
        } else {
          logout(true);
        }
      })
      .catch((err: unknown) => {
        // AC4 error handling: ServerError wraps HTTP-level failures with bodyText.
        // Non-ServerError = unexpected exception (network down, JS error) → show error page.
        if (!ServerError.is(err)) {
          history.replace('/error');
          setState((prev) => ({ ...prev, ready: true }));
          notifyIos({ message_type: 'ready' });
          return;
        }
        // Parse the server's JSON response to check for BadTokenError (expired/invalid token).
        // BadTokenError → gracefully end session (user sees login page).
        // Any other server error → show error page.
        let result: { name?: string } | undefined;
        try {
          result = JSON.parse((err as { bodyText: string }).bodyText) as { name?: string };
        } catch {
          // bodyText is not valid JSON
        }
        if (!result || result.name !== 'BadTokenError') {
          history.replace('/error');
          setState((prev) => ({ ...prev, ready: true }));
          notifyIos({ message_type: 'ready' });
          return;
        }
        logout(true);
      });
  } else {
    // No token available — redirect to login UNLESS on a public path that
    // doesn't require authentication to function.
    const pathname = window.location.pathname;
    if (
      pathname !== '/privacy' &&
      pathname !== '/delete-account' &&
      !pathname.startsWith('/otp') &&
      !pathname.startsWith('/reset_password') &&
      !pathname.startsWith('/sso') &&
      !pathname.includes('/devices') &&
      !pathname.includes('/unsubscribe_weekly_email')
    ) {
      history.replace('/login', { search: CLEAR_ALL_SEARCH_PARAMS });
    }
    setState((prev) => ({ ...prev, ready: true }));
    notifyIos({ message_type: 'ready' });
  }
}
